/**
 * tokenHandler — non-sensitive session storage + the single source of the
 * bearer token used for Billing API calls.
 *
 * The Platform sets httpOnly auth cookies for the browser, but the Billing API
 * expects `Authorization: Bearer <token>`. So after login (and on boot via
 * GET /v1/auth/token) we cache the platform access token here; the billing
 * axios instance reads it from exactly this one place — change the source here
 * and every billing call follows.
 */
import AsyncStorage from '@react-native-async-storage/async-storage';

const KEYS = {
  accessToken: 'skbill-access-token',
  orgId: 'skbill-org-id',
  businessName: 'skbill-business-name',
  firstName: 'skbill-first-name',
  lastName: 'skbill-last-name',
  role: 'skbill-role',
} as const;

// In-memory cache so the axios interceptor can read the token synchronously.
let accessTokenCache: string | null = null;

export const TokenHandler = {
  async setAccessToken(token: string | null): Promise<void> {
    accessTokenCache = token;
    if (token) await AsyncStorage.setItem(KEYS.accessToken, token);
    else await AsyncStorage.removeItem(KEYS.accessToken);
  },
  getAccessTokenSync(): string | null {
    return accessTokenCache;
  },
  async getAccessToken(): Promise<string | null> {
    if (accessTokenCache) return accessTokenCache;
    const t = await AsyncStorage.getItem(KEYS.accessToken);
    accessTokenCache = t;
    return t;
  },

  setOrgId: (v: string) => AsyncStorage.setItem(KEYS.orgId, v),
  getOrgId: () => AsyncStorage.getItem(KEYS.orgId),
  setBusinessName: (v: string) => AsyncStorage.setItem(KEYS.businessName, v),
  getBusinessName: () => AsyncStorage.getItem(KEYS.businessName),
  setFirstName: (v: string) => AsyncStorage.setItem(KEYS.firstName, v),
  getFirstName: () => AsyncStorage.getItem(KEYS.firstName),
  setLastName: (v: string) => AsyncStorage.setItem(KEYS.lastName, v),
  getLastName: () => AsyncStorage.getItem(KEYS.lastName),
  setRole: (v: string) => AsyncStorage.setItem(KEYS.role, v),
  getRole: () => AsyncStorage.getItem(KEYS.role),

  async clear(): Promise<void> {
    accessTokenCache = null;
    await Promise.all(Object.values(KEYS).map((k) => AsyncStorage.removeItem(k)));
  },
};
